import React, { useMemo, useState, useCallback, useEffect } from 'react';

import { api } from '../../api/api';
import { deriveDefaultsFromGlobals } from '../../utils/defaults';
import { buildTemplatePayload } from '../../utils/templatePayload';
import { readSelectedEventScope } from '../../utils/eventScopeStorage';
import TemplateMoveDialog from './TemplateMoveDialog';
import ManualSourceEditorModal from './ManualSourceEditorModal';

const describeVisibility = (visibility, groupLookup) => {
  if (!visibility) return 'Draft (unsaved)';
  if (visibility === 'global') return 'Global template';
  if (visibility === 'user') return 'My template';
  if (typeof visibility === 'string' && visibility.startsWith('group-')) {
    const id = visibility.replace('group-', '');
    const groupName = groupLookup?.(id);
    return groupName ? `Shared with ${groupName}` : 'Shared with a group';
  }
  return visibility;
};

const badgeClasses = visibility => {
  if (visibility === 'global') return 'bg-white/25 text-blue-900';
  if (visibility === 'user') return 'bg-white/25 text-slate-800';
  if (visibility && visibility.startsWith('group-'))
    return 'bg-emerald-100/60 text-emerald-700';
  return 'bg-white/25 text-slate-700';
};

const formatUpdatedAt = value => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const TemplateToolbar = ({
  settings,
  onUpdate,
  user,
  showNotification,
  onNavigate,
  persistVisualEditor,
  onTemplatesSaved,
  events = [],
}) => {
  const [isSavingExisting, setIsSavingExisting] = useState(false);
  const [isSavingCopy, setIsSavingCopy] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [isMoveDialogOpen, setMoveDialogOpen] = useState(false);
  const [isSourceEditorOpen, setSourceEditorOpen] = useState(false);
  const [isSourceSaving, setIsSourceSaving] = useState(false);
  const [groups, setGroups] = useState([]);

  const name = settings?.name?.trim() || 'Untitled design';
  const visibility = settings?.visibility;
  const lastUpdated =
    settings?.updatedAt || settings?.updated_at || settings?.visualEditor?.savedAt;
  const templateId = settings?.id && settings.id !== 'none' ? settings.id : null;
  const canUpdateExisting = Boolean(user && templateId && settings?.id !== 'new');
  const statusLabel = templateId ? `Template #${templateId}` : 'Draft not saved yet';
  const isAdmin = (user?.role?.name || '').toLowerCase() === 'admin';

  const flushVisualEditor = useCallback(() => {
    if (typeof persistVisualEditor === 'function') {
      try {
        return persistVisualEditor(true);
      } catch (error) {
        console.warn('Failed to persist visual editor before saving', error);
      }
    }
    return null;
  }, [persistVisualEditor]);

  const handleSaveExisting = useCallback(async () => {
    if (!canUpdateExisting) return;
    setIsSavingExisting(true);
    try {
      const visualPayload = flushVisualEditor();
      if (visualPayload) {
        onUpdate('visualEditor', visualPayload);
      }
      const payload = buildTemplatePayload(settings, {
        includeId: true,
        overrides: {
          visualEditor: visualPayload || settings?.visualEditor,
        },
      });
      const result = await api.saveTemplate(user.id, payload);
      if (result.success) {
        showNotification('Template updated successfully.', 'success');
        onTemplatesSaved?.();
        if (result?.template?.updatedAt || result?.template?.updated_at) {
          onUpdate('updatedAt', result.template.updatedAt || result.template.updated_at);
        } else if (visualPayload?.savedAt) {
          onUpdate('updatedAt', visualPayload.savedAt);
        }
      } else {
        showNotification(result.message || 'Failed to save template.', 'error');
      }
    } catch (error) {
      console.error('Failed to update template', error);
      showNotification(error?.message || 'Failed to save template.', 'error');
    } finally {
      setIsSavingExisting(false);
    }
  }, [
    canUpdateExisting,
    flushVisualEditor,
    onTemplatesSaved,
    onUpdate,
    settings,
    showNotification,
    user?.id,
  ]);

  const handleSaveAsCopy = useCallback(async () => {
    if (!user) {
      showNotification('You must be signed in to create templates.', 'error');
      return;
    }
    const selectedEventScope = readSelectedEventScope(user?.id, null);
    if (!selectedEventScope || selectedEventScope === 'all') {
      showNotification(
        'Create or select an event first. Saving to Global is not allowed.',
        'error'
      );
      return;
    }

    const defaultName = settings?.name ? `${settings.name} Copy` : 'New template copy';
    const nameInput =
      typeof window !== 'undefined'
        ? window.prompt('Name for the new template', defaultName)
        : defaultName;
    if (nameInput === null) return;
    const normalizedName = nameInput.trim();
    if (!normalizedName) {
      showNotification('Template name is required.', 'error');
      return;
    }

    setIsSavingCopy(true);
    try {
      const visualPayload = flushVisualEditor();
      if (visualPayload) {
        onUpdate('visualEditor', visualPayload);
      }
      const targetEventId =
        selectedEventScope && selectedEventScope !== 'all'
          ? Number.parseInt(selectedEventScope, 10)
          : null;
      const payload = buildTemplatePayload(settings, {
        includeId: false,
        overrides: {
          name: normalizedName,
          visibility: 'user',
          owner_id: user.id,
          event_id: Number.isInteger(targetEventId) ? targetEventId : null,
          visualEditor: visualPayload || settings?.visualEditor,
        },
      });

      const result = await api.saveTemplate(user.id, payload);
      if (result.success) {
        const savedTemplate = result?.template || null;
        const newId = savedTemplate?.id || result?.id || null;
        const newName = savedTemplate?.name || normalizedName;
        const promptMessage = `Continue editing: "${newName}"?`;
        const shouldContinue = typeof window === 'undefined' ? true : window.confirm(promptMessage);

        if (shouldContinue) {
          const nextSettings = {
            ...settings,
            ...(savedTemplate || {}),
            id: newId ?? settings.id,
            name: newName,
            visibility: savedTemplate?.visibility || 'user',
            owner_id: savedTemplate?.owner_id || user.id,
            visualEditor: savedTemplate?.visualEditor || visualPayload || settings?.visualEditor,
            updatedAt:
              savedTemplate?.updatedAt ||
              savedTemplate?.updated_at ||
              visualPayload?.savedAt ||
              Date.now(),
          };

          onUpdate(null, nextSettings);
          onNavigate?.('visualEditor');
          showNotification(`Template saved as new. Now editing "${newName}".`, 'success');
        } else {
          showNotification('Template saved as new.', 'success');
        }

        onTemplatesSaved?.();
      } else {
        showNotification(result.message || 'Failed to save template.', 'error');
      }
    } catch (error) {
      console.error('Failed to save template copy', error);
      showNotification(error?.message || 'Failed to save template.', 'error');
    } finally {
      setIsSavingCopy(false);
    }
  }, [
    flushVisualEditor,
    onNavigate,
    onTemplatesSaved,
    onUpdate,
    settings,
    showNotification,
    user,
  ]);

  const handleResetToBlank = useCallback(async () => {
    setIsResetting(true);
    try {
      const globals = await api.getGlobalSettings();
      const defaults = deriveDefaultsFromGlobals(globals);
      onUpdate(null, defaults);
      showNotification('Loaded default blank template.', 'success');
      onTemplatesSaved?.();
    } catch (error) {
      console.error('Failed to reset template', error);
      showNotification('Failed to load default template.', 'error');
    } finally {
      setIsResetting(false);
    }
  }, [onTemplatesSaved, onUpdate, showNotification]);

  useEffect(() => {
    if (!user) {
      setGroups([]);
      return;
    }
    let isCancelled = false;
    api
      .getGroups()
      .then(data => {
        if (!isCancelled) setGroups(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!isCancelled) setGroups([]);
      });
    return () => {
      isCancelled = true;
    };
  }, [user]);

  const groupNameResolver = useCallback(
    id => {
      if (!id) return null;
      const normalized = String(id);
      const group = groups.find(g => String(g.id) === normalized);
      return group ? group.name : null;
    },
    [groups]
  );

  const quickVisibilityLabel = useMemo(
    () => describeVisibility(visibility, groupNameResolver),
    [visibility, groupNameResolver]
  );

  const fetchSnapshotReadySettings = useCallback(async () => {
    const visualPayload = flushVisualEditor();
    let merged = settings;
    if (visualPayload) {
      onUpdate('visualEditor', visualPayload);
      merged = { ...settings, visualEditor: visualPayload };
    }
    try {
      return JSON.parse(JSON.stringify(merged));
    } catch (error) {
      console.warn('Failed to clone settings for snapshot', error);
      return merged;
    }
  }, [flushVisualEditor, onUpdate, settings]);

  const handleApplySnapshot = useCallback(
    snapshotSettings => {
      if (!snapshotSettings) return;
      onUpdate(null, snapshotSettings);
    },
    [onUpdate]
  );

  const visualEditorSnapshot = useMemo(() => {
    const visualEditor = settings?.visualEditor;
    if (!visualEditor || typeof visualEditor !== 'object') {
      return { html: '', css: '', variantKey: 'manual' };
    }
    if (visualEditor.variants && typeof visualEditor.variants === 'object') {
      const variantKeys = Object.keys(visualEditor.variants);
      const activeKey =
        visualEditor.activeVariant &&
        visualEditor.variants[visualEditor.activeVariant]
          ? visualEditor.activeVariant
          : variantKeys[0] || 'manual';
      const activeVariant =
        visualEditor.variants[activeKey] || visualEditor.variants[variantKeys[0]] || {};
      return {
        html:
          activeVariant.html ||
          activeVariant.rawHtml ||
          visualEditor.html ||
          visualEditor.rawHtml ||
          '',
        css: activeVariant.css || visualEditor.css || '',
        variantKey: activeKey || 'manual',
      };
    }
    return {
      html: visualEditor.html || visualEditor.rawHtml || '',
      css: visualEditor.css || '',
      variantKey: visualEditor.activeVariant || 'manual',
    };
  }, [settings?.visualEditor]);

  const handleOpenSourceEditor = useCallback(() => {
    if (!canUpdateExisting) {
      showNotification('Save the template before editing the source code.', 'error');
      return;
    }
    setSourceEditorOpen(true);
  }, [canUpdateExisting, showNotification]);

  const handleManualSourceSave = useCallback(
    async ({ html, css, variantKey }) => {
      if (!templateId) {
        showNotification('Select a saved template first.', 'error');
        return;
      }
      setIsSourceSaving(true);
      try {
        const result = await api.updateTemplateSource(templateId, {
          html,
          css,
          variantKey,
        });
        const serverTemplate = result?.template;
        const serverData = serverTemplate?.data || {};
        const nextVisualEditor = serverData.visualEditor || settings?.visualEditor;
        const mergedSettings = {
          ...settings,
          ...serverData,
          id: serverTemplate?.id || settings?.id,
          name: serverTemplate?.name || settings?.name,
          visibility: serverTemplate?.visibility || settings?.visibility,
          event_id:
            serverTemplate?.event_id !== undefined
              ? serverTemplate?.event_id
              : settings?.event_id,
          visualEditor: nextVisualEditor,
          updatedAt:
            serverTemplate?.updatedAt ||
            serverTemplate?.updated_at ||
            nextVisualEditor?.savedAt ||
            Date.now(),
        };
        onUpdate(null, mergedSettings);
        showNotification('Template source updated.', 'success');
        onTemplatesSaved?.();
        setSourceEditorOpen(false);
      } catch (error) {
        console.error('Failed to update template source', error);
        showNotification(error?.message || 'Failed to update template source.', 'error');
      } finally {
        setIsSourceSaving(false);
      }
    },
    [onTemplatesSaved, onUpdate, settings, showNotification, templateId]
  );

  const handleOpenMove = useCallback(() => {
    if (!canUpdateExisting) {
      showNotification('Select a saved template first.', 'error');
      return;
    }
    setMoveDialogOpen(true);
  }, [canUpdateExisting, showNotification]);

  return (
    <div className="glass-panel-strong px-4 py-4 space-y-4 text-slate-800 md:px-6 md:py-5">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="space-y-3 md:space-y-2">
          <div className="flex flex-wrap items-center gap-2 text-[11px] tracking-wide text-slate-500">
            <span className="section-title">Template Overview</span>
            <span className={`pill-tag px-2 py-1 text-[11px] ${badgeClasses(visibility)}`}>
              {quickVisibilityLabel}
            </span>
            <span className="pill-tag bg-white/30 px-2 py-1 text-[11px] text-slate-700">{statusLabel}</span>
            {lastUpdated && (
              <span className="pill-tag bg-white/30 px-2 py-1 text-[11px] text-slate-700">
                Visual editor saved {formatUpdatedAt(lastUpdated)}
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-baseline gap-3">
            <h1 className="section-heading text-xl md:text-2xl">{name}</h1>
          </div>
        </div>
        <div className="flex flex-col gap-2 md:flex-row md:flex-wrap md:items-center md:justify-end">
          <button
            type="button"
            onClick={() => onNavigate('templateSelect')}
            className="toolbar-action toolbar-action--ghost w-full sm:w-auto"
          >
            Template library
          </button>
          <button
            type="button"
            onClick={() => onNavigate('visualEditor')}
            className="toolbar-action toolbar-action--ghost w-full sm:w-auto"
          >
            Visual editor
          </button>
          {isAdmin && (
            <button
              type="button"
              onClick={handleOpenSourceEditor}
              className="toolbar-action toolbar-action--ghost w-full sm:w-auto"
              disabled={!canUpdateExisting}
            >
              Edit source
            </button>
          )}
          <button
            type="button"
            onClick={handleOpenMove}
            className="toolbar-action toolbar-action--move w-full sm:w-auto"
            disabled={!canUpdateExisting}
          >
            Move template
          </button>
          <button
            type="button"
            onClick={handleResetToBlank}
            className="toolbar-action toolbar-action--reset w-full sm:w-auto"
            disabled={isResetting}
          >
            {isResetting ? 'Loading…' : 'Reset to blank'}
          </button>
          <button
            type="button"
            onClick={handleSaveAsCopy}
            className="toolbar-action toolbar-action--save w-full sm:w-auto"
            disabled={isSavingCopy}
          >
            {isSavingCopy ? 'Saving…' : 'Save as new'}
          </button>
          <button
            type="button"
            onClick={handleSaveExisting}
            className="nav-button-primary w-full sm:w-auto"
            disabled={!canUpdateExisting || isSavingExisting}
            title={
              canUpdateExisting
                ? 'Update the currently selected template'
                : 'Load an existing template first to enable quick save'
            }
          >
            {isSavingExisting ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
      {isMoveDialogOpen && (
        <TemplateMoveDialog
          open={isMoveDialogOpen}
          onClose={() => setMoveDialogOpen(false)}
          template={settings}
          user={user}
          showNotification={showNotification}
          onMoved={targetEventId => {
            onTemplatesSaved?.();
            onUpdate('event_id', targetEventId);
            onUpdate('eventId', targetEventId);
          }}
          fallbackEvents={events}
          shouldAutoFetch={events.length === 0}
        />
      )}
      {isAdmin && (
        <ManualSourceEditorModal
          open={isSourceEditorOpen}
          onClose={() => !isSourceSaving && setSourceEditorOpen(false)}
          initialHtml={visualEditorSnapshot.html}
          initialCss={visualEditorSnapshot.css}
          initialVariantKey={visualEditorSnapshot.variantKey}
          onSave={handleManualSourceSave}
          saving={isSourceSaving}
        />
      )}
    </div>
  );
};

export default TemplateToolbar;
